import traceHeadIcon from "@/assets/leaflet/traceHead";
import useTraces from "@/hooks/useTraces";
import useGlobalStore from "@/services/store/useGlobalStore";
import useMapMode from "@/services/store/useMapMode";
import usePlayer from "@/services/store/usePlayer";
import { formatTimestamp } from "@/utils/functions/date";
import { exportJson } from "@/utils/functions/exportJson";
import { pointsToLatLngExpression } from "@/utils/functions/trace";
import { Box, ClickAwayListener } from "@mui/material";
import L, { LatLngExpression } from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, Marker, Polyline, Popup } from "react-leaflet";
import RightClickPopup from "../components/RightClickPopup";
import LinesAndPoints from "./LinesAndPoints";
import circleIcon from "@/assets/leaflet/circleIcon";

interface ITrace {
  user: UserWithColor | null;
  trace: Trace;
  selectable: boolean;
}

const Trace: React.FC<ITrace> = ({ user, trace, selectable }) => {
  const polylineRef = useRef(null);
  const markerRef = useRef(null);
  const [popupLatLng, setPopupLatLng] = useState<LatLngExpression | undefined>();

  const { addSelectedTrace, selectTrace, deselectTraces, deselectTrace, selectedTraceIds } = useGlobalStore(
    (state) => state
  );

  const { deleteTrace, reverseTrace } = useTraces();

  const isSelected = selectedTraceIds.length > 0 && !!trace?.id && !!selectedTraceIds.includes(trace.id);

  const isPlaying = usePlayer((state) => state.isPlaying);
  const timestamp = usePlayer((state) => state.timestamp);

  const isMapDragging = useMapMode((state) => state.isMapDragging);

  const deleteUserTrace = useCallback(() => {
    if (trace) {
      deselectTrace(trace.id);
      deleteTrace(trace);
    }
  }, [trace]);

  const reverseUserTrace = useCallback(() => {
    if (!trace) return;
    if (trace.points.length > 1) {
      reverseTrace(trace);
    }
    setPopupLatLng(undefined);
    deselectTrace(trace.id);
  }, [trace]);

  const getTraceName = (trace: Trace) => {
    let prefix = "";
    if (user) {
      prefix = user.alias ?? user.username;
    }
    const timestamp = trace?.points?.[0]?.timestamp ?? "";
    return `${prefix}_${formatTimestamp(timestamp)}`;
  };

  const saveUserTrace = useCallback(() => {
    if (trace) {
      exportJson(trace, `${getTraceName(trace)}.json`);
    }
    setPopupLatLng(undefined);
  }, [trace]);

  const actions = { deleteTrace: deleteUserTrace, reverseTrace: reverseUserTrace, saveTrace: saveUserTrace };

  const showPopup = (e: any) => {
    selectUserTrace(e);
    setPopupLatLng(e.latlng);
  };

  /**
   * Select one or many traces based on ctrlKey pressed or not.
   * @param e The event -- Set to any because MouseEvent does not correspond...
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectUserTrace = (e: any) => {
    if (!trace) return;

    // ctrlKey pressed, and selected, remove from selection
    if (e.originalEvent.ctrlKey && isSelected) {
      deselectTrace(trace.id);
    }
    // ctrlKey pressed and not selected, add in selection
    if (e.originalEvent.ctrlKey && !isSelected) {
      addSelectedTrace(trace.id);
    }

    // ctrlKey not pressed, simply select only this trace
    if (!e.originalEvent.ctrlKey) {
      selectTrace(trace.id);
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (isMapDragging) return;
    const target = event.target as HTMLElement;
    if (target.getAttribute("id") === "map") deselectTraces();
  };

  const removeFuturePointsFromSelectedTrace = (trace: Trace) => {
    const points = trace.points ? trace.points.sort((a, b) => a.timestamp - b.timestamp) : [];
    if (timestamp === 0 || !isSelected) return pointsToLatLngExpression(points);
    return pointsToLatLngExpression(points.filter((point) => point.timestamp <= timestamp));
  };

  const getMarkerPosition = (trace: Trace) => {
    if (timestamp === 0 || !isSelected) return trace?.points?.[trace.points.length - 1];
    const filteredTracePoints = trace.points.filter((point) => point.timestamp <= timestamp);
    return filteredTracePoints?.[filteredTracePoints.length - 1];
  };

  const markerPosition = getMarkerPosition(trace);

  const tracePoints = removeFuturePointsFromSelectedTrace(trace);

  const userName = useMemo(() => user?.alias ?? "Non assignée", [user]);

  // --- Alert button effects ---

  const shortPressPoints = useMemo(
    () => trace.points.filter((p) => p.buttonStatus === "SHORT_PRESS"),
    [trace.points]
  );

  const longPressLines = useMemo(
    () =>
      trace.points
        .filter((p) => p.buttonStatus === "LONG_PRESS")
        .map((p) => {
          const rRad = (p.r * Math.PI) / 180;
          const latRad = (p.x * Math.PI) / 180;
          // 1° latitude ≈ 111 000 m; 1° longitude ≈ 111 000 * cos(lat) m
          const metersPerDegLat = 111_000;
          const metersPerDegLon = 111_000 * Math.cos(latRad);
          const endLat = p.x + (Math.cos(rRad) * 100) / metersPerDegLat;
          const endLon = p.y + (Math.sin(rRad) * 100) / metersPerDegLon;
          return { id: p.id, from: [p.x, p.y] as [number, number], to: [endLat, endLon] as [number, number] };
        }),
    [trace.points]
  );

  const isPopupOpened = useMemo(() => !!popupLatLng, [popupLatLng]);

  const formattedTraceHeadIcon = new L.Icon({
    iconUrl:
      trace.points.length > 1
        ? traceHeadIcon(user?.color ?? "black", markerPosition?.r ?? 0)
        : circleIcon(isSelected ? "red" : user?.color ?? "black"),
    iconSize: [22, 22],
  });

  useEffect(() => {
    if (polylineRef.current && markerRef.current) {
      const polyline: HTMLElement = polylineRef.current;
      polyline.addEventListener("contextmenu", showPopup);
      if (selectable) {
        polyline.addEventListener("click", selectUserTrace);
      }

      const marker: HTMLElement = markerRef.current;
      marker.addEventListener("contextmenu", showPopup);
      if (selectable) {
        marker.addEventListener("click", selectUserTrace);
      }

      return () => {
        polyline.removeEventListener("contextmenu", showPopup);
        marker.removeEventListener("contextmenu", showPopup);
        if (selectable) {
          polyline.removeEventListener("click", selectUserTrace);
          marker.removeEventListener("click", selectUserTrace);
        }
      };
    }
  }, [polylineRef, markerRef, selectedTraceIds, tracePoints]);

  useEffect(() => {
    if (isPlaying && isSelected) setPopupLatLng(undefined);
  }, [isSelected, isPlaying]);

  return (
    <Box>
      {isPopupOpened ? (
        <Popup position={popupLatLng} minWidth={150} maxWidth={250} closeButton={false}>
          <RightClickPopup userName={userName} actions={actions} />
        </Popup>
      ) : null}
      <Polyline
        className="test"
        pathOptions={{ color: !isSelected ? user?.color ?? "black" : "transparent" }}
        positions={tracePoints}
        noClip
      />
      {selectable && (
        <Polyline
          dashArray="10, 10"
          positions={tracePoints}
          pathOptions={{
            color: isSelected ? user?.color ?? "black" : "transparent",
          }}
        />
      )}
      {selectable && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Polyline
            ref={polylineRef}
            positions={tracePoints}
            pathOptions={{
              color: "transparent",
            }}
            weight={25}
          />
        </ClickAwayListener>
      )}
      <LinesAndPoints points={tracePoints} color={user?.color ?? "black"} />

      {markerPosition ? (
        <Marker ref={markerRef} position={[markerPosition?.x, markerPosition?.y]} icon={formattedTraceHeadIcon} />
      ) : null}

      {/* SHORT_PRESS: persistent pink dot at each alert point */}
      {shortPressPoints.map((p) => (
        <CircleMarker
          key={`shortpress-${p.id}`}
          center={[p.x, p.y]}
          radius={5}
          pathOptions={{ color: "#FF1493", fillColor: "#FF1493", fillOpacity: 1, weight: 1 }}
        />
      ))}

      {/* LONG_PRESS: 100 m directional line per point */}
      {longPressLines.map((line) => (
        <Polyline
          key={`longpress-${line.id}`}
          positions={[line.from, line.to]}
          pathOptions={{ color: user?.color ?? "white", weight: 2, opacity: 0.85, dashArray: "6, 4" }}
        />
      ))}
    </Box>
  );
};

export default Trace;
